import React, { useMemo, useState } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  Typography,
  MenuItem,
  Autocomplete,
  Skeleton,
} from "@mui/material";
import {
  StyledTextField,
  StyledSelect,
} from "../../customComponents/styled/styledInputs";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as yup from "yup";
import {
  FilledButton,
  StyledLoadingButton,
} from "../../customComponents/styled/styledButtons";
import {
  useUpdateSubjectMutation,
  useGetSubjectClassroomsQuery,
} from "./subjectsApiSlice";
import toast from "../../../helpers/toast";
import { useGetTeachersPaginatedQuery } from "../staff/staffApiSlice";
import {
  useAddSubjectToClassroomMutation,
  useRemoveSubjectFromClassroomMutation,
  useGetClassesQuery,
} from "../classroom/classApiSlice";

const validationSchema = yup.object({
  name: yup.string().required("Subject name is required"),
  description: yup.string(),
  classes: yup.array().of(
    yup.object().shape({
      class: yup.string().required("Required"),
      teacher: yup.string().required("Required"),
    }),
  ),
});

function EditSubjectForm({ focusedSubject, setOpenEdit, setFocusedSubject }) {
  const { data: classrooms } = useGetClassesQuery("all");
  const [teacherSearch, setTeacherSearch] = useState("");
  const { data: teachersData } = useGetTeachersPaginatedQuery({
    page: 1,
    per_page: 10,
    q: teacherSearch,
  });
  const teachers = teachersData?.teachers ?? [];

  const { data: mappedClassrooms, isFetching: isLoadingClassrooms } =
    useGetSubjectClassroomsQuery(focusedSubject?.id, {
      skip: !focusedSubject?.id,
    });

  const initialClasses = useMemo(() => {
    if (mappedClassrooms && mappedClassrooms.length > 0) {
      return mappedClassrooms.map((c) => ({
        class: c.class,
        teacher: c.teacher,
      }));
    }
    return [{ class: "", teacher: "" }];
  }, [mappedClassrooms]);

  const [updateSubject, { isLoading }] = useUpdateSubjectMutation();
  const [addSubjectToClass, { isLoading: addLoading }] =
    useAddSubjectToClassroomMutation();
  const [removeSubjectFromClass, { isLoading: removeLoading }] =
    useRemoveSubjectFromClassroomMutation();

  const formik = useFormik({
    initialValues: {
      id: focusedSubject.id,
      name: focusedSubject.name,
      description: focusedSubject.description,
      classes: initialClasses,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const isSubjectChanged =
          values.name !== focusedSubject.name ||
          values.description !== focusedSubject.description;

        if (isSubjectChanged) {
          await updateSubject({
            id: values.id,
            name: values.name,
            description: values.description,
          }).unwrap();
        }

        const oldMap = new Map();
        initialClasses.forEach((c) => {
          if (c.class) oldMap.set(c.class, c.teacher);
        });

        const newMap = new Map();
        values.classes.forEach((c) => {
          if (c.class) newMap.set(c.class, c.teacher);
        });

        const promises = [];

        // Find removed
        for (let oldClass of oldMap.keys()) {
          if (!newMap.has(oldClass)) {
            promises.push(
              removeSubjectFromClass({
                class_Id: oldClass,
                subject_Id: focusedSubject.id,
              }).unwrap(),
            );
          }
        }

        // Find added or updated teacher
        for (let [newClass, newTeacher] of newMap.entries()) {
          const oldTeacher = oldMap.get(newClass);
          if (oldTeacher !== newTeacher) {
            promises.push(
              addSubjectToClass({
                class_Id: newClass,
                subject_id: focusedSubject.id,
                teacher_id: newTeacher,
              }).unwrap(),
            );
          }
        }

        await Promise.all(promises);

        toast.success("Subject updated successfully");
        setFocusedSubject(null);
        setOpenEdit(false);
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  return (
    <>
      <Box
        className="paperHeader"
        sx={{ display: "flex", justifyContent: "space-between" }}
      >
        <Typography variant="formHeading">Edit Subject</Typography>
        <FilledButton
          onClick={() => {
            setFocusedSubject(null);
            setOpenEdit(false);
          }}
        >
          Close
        </FilledButton>
      </Box>
      <Box className="paperBody">
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Grid container columnSpacing={1} rowSpacing={3}>
            <Grid item xs={12}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">Subject Name</Typography>
                <StyledTextField
                  autoFocus
                  variant="outlined"
                  id="name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                />
                <FormHelperText variant="error">
                  {formik.touched.name && formik.errors.name}
                </FormHelperText>
              </FormControl>
            </Grid>

            {isLoadingClassrooms ? (
              <Grid item container xs={12} columnSpacing={5}>
                <Grid item xs={5}>
                  <Skeleton variant="text" height={80} />
                </Grid>
                <Grid item xs={5}>
                  <Skeleton variant="text" height={80} />
                </Grid>
              </Grid>
            ) : (
              <FormikProvider value={formik}>
                <FieldArray
                  name="classes"
                  render={(arrayHelpers) => {
                    const classes = formik.values.classes;
                    return (
                      <>
                        {classes && classes.length > 0
                          ? classes.map((row, index) => (
                              <React.Fragment key={index}>
                                <Grid item xs={12} md={5}>
                                  <FormControl
                                    error={
                                      formik.touched.classes?.[index]?.class &&
                                      formik.errors.classes?.[index]?.class
                                    }
                                    fullWidth
                                  >
                                    <Typography
                                      htmlFor="class"
                                      variant="formSubHeading"
                                    >
                                      Class
                                    </Typography>
                                    <StyledSelect
                                      displayEmpty
                                      id="class"
                                      name={`classes.${index}.class`}
                                      value={row.class}
                                      onChange={formik.handleChange}
                                      error={
                                        formik.touched.classes?.[index]
                                          ?.class &&
                                        Boolean(
                                          formik.errors.classes?.[index]?.class,
                                        )
                                      }
                                    >
                                      <MenuItem value="" disabled>
                                        <em>select classroom</em>
                                      </MenuItem>
                                      {classrooms?.map((classroom) => (
                                        <MenuItem
                                          key={classroom.id}
                                          value={classroom.id}
                                        >
                                          {classroom.name}
                                        </MenuItem>
                                      ))}
                                    </StyledSelect>
                                    <FormHelperText variant="error">
                                      {formik.touched.classes?.[index]?.class &&
                                        formik.errors.classes?.[index]?.class}
                                    </FormHelperText>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={12} md={5}>
                                  <FormControl
                                    error={
                                      formik.touched.classes?.[index]
                                        ?.teacher &&
                                      formik.errors.classes?.[index]?.teacher
                                    }
                                    fullWidth
                                  >
                                    <Typography
                                      htmlFor="teacher"
                                      variant="formSubHeading"
                                    >
                                      Teacher
                                    </Typography>
                                    <Autocomplete
                                      options={teachers}
                                      getOptionLabel={(option) =>
                                        option
                                          ? `${option.first_name} ${option.last_name}`
                                          : ""
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option.user_id === value.user_id
                                      }
                                      value={
                                        teachers.find(
                                          (t) => t.user_id === row.teacher,
                                        ) || null
                                      }
                                      onChange={(_, value) =>
                                        formik.setFieldValue(
                                          `classes.${index}.teacher`,
                                          value?.user_id || "",
                                        )
                                      }
                                      onInputChange={(_, value, reason) => {
                                        if (reason === "input")
                                          setTeacherSearch(value);
                                      }}
                                      filterOptions={(x) => x}
                                      renderInput={(params) => (
                                        <StyledTextField
                                          {...params}
                                          placeholder="Search teacher..."
                                          error={
                                            formik.touched.classes?.[index]
                                              ?.teacher &&
                                            Boolean(
                                              formik.errors.classes?.[index]
                                                ?.teacher,
                                            )
                                          }
                                        />
                                      )}
                                    />
                                    <FormHelperText variant="error">
                                      {formik.touched.classes?.[index]
                                        ?.teacher &&
                                        formik.errors.classes?.[index]?.teacher}
                                    </FormHelperText>
                                  </FormControl>
                                </Grid>

                                <Grid
                                  item
                                  xs={12}
                                  md={2}
                                  sx={{
                                    display: {
                                      md: "flex",
                                    },
                                    alignItems: "flex-end",
                                    justifyContent: "flex-end",
                                    mb: 0.5,
                                  }}
                                >
                                  {index === 0 ? (
                                    <FilledButton
                                      onClick={() =>
                                        arrayHelpers.push({
                                          class: "",
                                          teacher: "",
                                        })
                                      }
                                    >
                                      +
                                    </FilledButton>
                                  ) : (
                                    <FilledButton
                                      onClick={() => arrayHelpers.remove(index)}
                                    >
                                      x
                                    </FilledButton>
                                  )}
                                </Grid>
                              </React.Fragment>
                            ))
                          : null}
                      </>
                    );
                  }}
                />
              </FormikProvider>
            )}

            <Grid item xs={12} md={12} textAlign={"right"}>
              <StyledLoadingButton
                type="submit"
                loading={isLoading || addLoading || removeLoading}
                disabled={!formik.dirty}
              >
                Update
              </StyledLoadingButton>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}

export default EditSubjectForm;
